'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import { getChat } from '../../lib/api/meridian-api-client.js';
import type { Chat } from '../../types/index.js';
import type { UIMessage } from 'ai';

export interface UseMessagesQueryOptions {
  apiBaseUrl?: string;
  chatId: string;
  limit?: number;
  enabled?: boolean;
}

export interface UseMessagesQueryResult {
  data: UIMessage[];
  chat: Chat | null;  // Full chat object
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * TanStack Query hook for fetching messages with infinite pagination
 * 
 * Features:
 * - Automatic caching with 1-minute stale time
 * - Infinite pagination support (loads older messages)
 * - Automatic refetch on window focus
 * - Exponential backoff retry on failures
 * - Prevents duplicate requests
 */
export function useMessagesQuery(options: UseMessagesQueryOptions): UseMessagesQueryResult {
  const {
    apiBaseUrl = '/api/meridian',
    chatId,
    limit = 10,
    enabled = true,
  } = options;

  const query = useInfiniteQuery({
    queryKey: ['messages', { apiBaseUrl, chatId, limit }],
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const response = await getChat(apiBaseUrl, chatId, {
        limit,
        cursor: pageParam,
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch messages');
      }

      const messages = response.data?.messages || [];
      return {
        messages,
        chat: response.data || null,  // Include full chat object
        cursor: response.cursor || null,
        hasMore: response.hasMore || false,
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled: enabled && !!chatId,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
    refetchOnWindowFocus: true,
    refetchOnMount: true, // Always refetch when component mounts with new chat ID (ensures fresh data on chat switch)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * Math.pow(2, attemptIndex), 30000),
  });

  // Flatten pages into single array (API returns messages with parts; cast to UIMessage[] for useChat)
  type Page = { messages: unknown[]; chat: Chat | null; cursor: string | null; hasMore: boolean };
  const allMessages = (query.data?.pages.flatMap((page: Page) => page.messages) || []) as UIMessage[];
  
  // Get the full chat object from the first page (it's the same for all pages)
  const chatObject = query.data?.pages[0]?.chat ?? null;

  return {
    data: allMessages,
    chat: chatObject,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasNextPage: query.hasNextPage ?? false,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: async () => {
      await query.fetchNextPage();
    },
    refetch: async () => {
      await query.refetch();
    },
  };
}
